import React, { useState, useEffect } from "react"
import Confetti from "react-confetti"
import { nextRound } from "./justOneRoom"
import { Screen, JoButton, PlayerDot, C, F } from "./JustOneUI"
import { playSound } from "../../../utils/ballparkSounds"

const RevealScreen = ({ room, code, playerId, isHost }) => {
  const r = room.lastResult
  const [showConfetti, setShowConfetti] = useState(false)

  // Get window size for Confetti
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 500,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  })

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight })
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Play result sound on load and trigger confetti if correct
  useEffect(() => {
    if (r) {
      if (r.guessStatus === "correct") {
        playSound("correct")
        setShowConfetti(true)
      } else if (r.guessStatus === "incorrect") {
        playSound("lose")
      } else {
        playSound("click")
      }
    }
  }, [r])

  if (!r) return null

  const lastRound = room.round >= room.totalRounds
  const guesserName = room.players?.[r.guesserId]?.name || r.guesserName || "Guesser"
  const isCorrect = r.guessStatus === "correct"
  const isPass = r.guessStatus === "passed"
  
  const players = Object.entries(room.players || {}).sort(
    (a, b) => a[1].joinedAt - b[1].joinedAt
  )

  const handleNext = async () => {
    playSound("click")
    await nextRound(code)
  }

  return (
    <Screen style={{ gap: 18, position: "relative", overflowX: "hidden" }}>
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={150}
        />
      )}

      {/* Header bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 16, color: C.muted }}>
          ROUND {room.round} OF {room.totalRounds} RECAP
        </div>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 16, color: C.teal }}>
          TEAM SCORE: {room.teamScore || 0}
        </div>
      </div>

      {/* Reveal Banner */}
      <div style={{ textAlign: "center", padding: "20px 10px", background: C.card, borderRadius: 20, border: `1.5px solid ${C.border}`, boxShadow: "0 4px 10px rgba(0,0,0,0.02)" }}>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: C.muted, fontFamily: F.display, marginBottom: 4 }}>
          SECRET WORD
        </div>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 36, color: C.ink, textTransform: "uppercase", letterSpacing: 1 }}>
          {r.secretWord}
        </div>

        <div style={{ height: 1, background: C.border, margin: "16px auto", width: "80%" }} />

        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: C.muted, fontFamily: F.display, marginBottom: 6 }}>
          {guesserName.toUpperCase()}'S GUESS
        </div>
        <div
          style={{
            fontFamily: F.display,
            fontWeight: 900,
            fontSize: 28,
            color: isCorrect ? C.green : isPass ? C.amber : C.coral,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {r.guess || "(None)"}
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 800,
            color: isCorrect ? C.greenDark : isPass ? C.amberDark : C.coralDark,
            marginTop: 4,
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          {isCorrect ? "✨ CORRECT! +1 TEAM POINT" : isPass ? "⚠️ PASSED" : "❌ INCORRECT"}
        </div>
      </div>

      {/* Clues Breakdown */}
      <div>
        <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: C.muted, fontFamily: F.display, marginBottom: 8 }}>
          CLUES & OUTCOMES
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {Object.entries(r.clues).map(([pid, clueText]) => {
            const player = room.players?.[pid] || { name: "Player", color: C.muted }
            const isEliminated = !!r.eliminatedClues?.[pid]
            const pointsGained = r.scoresGained?.[pid] || 0

            return (
              <div
                key={pid}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  background: isEliminated ? C.panel : C.card,
                  border: `1.5px solid ${isEliminated ? C.border : player.color}`,
                  borderRadius: 12,
                  padding: "8px 12px",
                  opacity: isEliminated ? 0.5 : 1,
                }}
              >
                <PlayerDot color={player.color} size={16} />
                <span style={{ fontWeight: 800, fontSize: 14, color: C.ink }}>{player.name}</span>
                <span
                  style={{
                    fontWeight: 900,
                    fontSize: 16,
                    color: isEliminated ? C.muted : C.ink,
                    textTransform: "uppercase",
                    textDecoration: isEliminated ? "line-through" : "none",
                    marginLeft: 12,
                  }}
                >
                  {clueText}
                </span>

                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
                  {isEliminated ? (
                    <span style={{ fontSize: 11, fontWeight: 800, color: C.coral, background: `${C.coral}15`, padding: "2px 6px", borderRadius: 6 }}>
                      DUPLICATE
                    </span>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 800, color: C.green, background: `${C.green}15`, padding: "2px 6px", borderRadius: 6 }}>
                      UNIQUE
                    </span>
                  )}
                  {pointsGained > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 900, color: C.teal }}>
                      +{pointsGained} pt
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Point additions for guesser */}
      {r.scoresGained?.[r.guesserId] > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: `${C.green}10`, borderRadius: 12, border: `1px solid ${C.green}40` }}>
          <PlayerDot color={room.players?.[r.guesserId]?.color || C.cyan} size={16} />
          <span style={{ fontSize: 13, fontWeight: 800, color: C.greenDark }}>
            {guesserName} got <strong>+{r.scoresGained[r.guesserId]} points</strong> for the correct guess!
          </span>
        </div>
      )}

      {/* Next Round controls */}
      <div style={{ marginTop: "auto" }}>
        {isHost ? (
          <JoButton onClick={handleNext} variant="cyan">
            {lastRound ? "Show Final Standings →" : "Start Next Round →"}
          </JoButton>
        ) : (
          <div style={{ textAlign: "center", fontWeight: 700, color: C.muted, fontFamily: F.display, padding: 12 }}>
            Waiting for the host to advance…
          </div>
        )}
      </div>
    </Screen>
  )
}

export default RevealScreen
